import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { RootState } from '../redux/store';
import { setTreatment } from '../redux/slices/appointmentSlice';
import { StepCard } from '../components/StepCard';
import { Button } from '../components/Button';
import { StepActions } from '../components/StepActions';
import { InputCard } from '../components/InputCard';
import { AppointmentType, PatientTypeEnum } from '../types';
import { Stethoscope } from 'lucide-react';

const isAvailableFor = (t: AppointmentType, patientType: string) => {
  const posting = t.allowedPatientPosting || 'both';
  if (posting === 'new_patients_only') return patientType === PatientTypeEnum.New;
  if (posting === 'existing_patients_only') return patientType === PatientTypeEnum.Existing;
  return true;
};

export const StepTreatment = () => {
  const selected = useSelector((s: RootState) => s.appointment.treatment);
  const patientType = useSelector((s: RootState) => s.appointment.patientType);
  const { practice } = useSelector((s: RootState) => s.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Patient type is chosen first so we only show the treatments this patient can book.
  const treatments = (practice?.appointment_types || []).filter(t => isAvailableFor(t, patientType));
  const selectedIsAvailable = !!selected && treatments.some(t => t.id === selected.id);

  React.useEffect(() => {
    if (!patientType) {
      navigate('../step-1', { replace: true });
    }
  }, [patientType, navigate]);

  const proceed = () => {
    if (selectedIsAvailable) {
      navigate(`../step-3`);
    }
  };

  return (
    <Layout>
      <div className="space-y-4">
        <div className='flex flex-col items-center text-center md:items-start md:text-start'>
          <h1 className="text-2xl font-semibold text-black">Treatment type</h1>
          <p className="text-sm text-gray-500">Choose the service you'd like to schedule</p>
        </div>
        <StepCard className="flex flex-col justify-between overflow-y-auto">
          {treatments.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {treatments.map(t => (
                <InputCard
                  key={t.id}
                  title={t.title}
                  isSelected={selected?.id === t.id}
                  onClick={() => dispatch(setTreatment(t))}
                  variant="treatment"
                  icon={<Stethoscope size={24} className="text-blue-600" />}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center my-auto">
              There are no appointment types available to book online for {patientType} patients. Please call the office to schedule.
            </p>
          )}
        </StepCard>

        <StepActions>
          <Button title='Back' variant='secondary' onClick={() => navigate('../step-1')} />
          <Button title='Continue' disabled={!selectedIsAvailable} onClick={proceed} />
        </StepActions>
      </div>
    </Layout>
  );
};
